import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  BookOpen,
  Calculator,
  CreditCard,
  HelpCircle,
  Map,
  ArrowLeft,
  PenLine,
  FileText,
  PencilLine,
  MessageSquare,
  Shuffle,
  Calendar,
  RefreshCw,
  Trophy,
  CheckCircle,
  RotateCcw,
  Settings,
  Languages,
  ChevronRight,
  MessageCircle,
  Music,
} from 'lucide-react';
import { useCourse } from '../hooks/useCourse';
import { useAuth } from '../hooks/useAuth';
import { getFlashcardProgress, getCourseCompletionStatus, resetCourseProgress, CourseCompletionStatus } from '../lib/supabase';
import { FlashcardProgress } from '../types';
import { ResetModal } from './ResetModal';
import { CourseProgressCard, ModuleProgress } from './CourseProgressCard';

export function CourseView() {
  const { courseId } = useParams<{ courseId: string }>();
  const { course, isLoading, error } = useCourse(courseId || '');
  const { user } = useAuth();
  const [weaknessCount, setWeaknessCount] = useState(0);
  const [completionStatus, setCompletionStatus] = useState<CourseCompletionStatus | null>(null);
  const [showResetMessage, setShowResetMessage] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [moduleProgress, setModuleProgress] = useState<ModuleProgress | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [topicWeaknessMap, setTopicWeaknessMap] = useState<Record<string, number>>({});

  // Load weakness count and completion status
  useEffect(() => {
    async function loadProgressData() {
      if (!user?.id || !courseId) return;

      // Load flashcard weaknesses
      const progress = await getFlashcardProgress(user.id, courseId);
      const weakCount = progress.filter(
        (p: FlashcardProgress) => !p.knew && p.flashcard_id.startsWith('vocab_')
      ).length;
      setWeaknessCount(weakCount);

      // Build per-topic flashcard weakness counts (for courses with categories)
      if (course?.categories && course.categories.length > 0) {
        const weakIds = progress
          .filter((p: FlashcardProgress) => !p.knew && !p.flashcard_id.startsWith('vocab_'))
          .map((p: FlashcardProgress) => p.flashcard_id);
        const map: Record<string, number> = {};
        for (const fc of course.flashcards) {
          if (weakIds.includes(fc.id)) {
            map[fc.category] = (map[fc.category] || 0) + 1;
          }
        }
        setTopicWeaknessMap(map);
      }

      // Load completion status
      const status = await getCourseCompletionStatus(user.id, courseId);
      setCompletionStatus(status);
    }

    loadProgressData();
  }, [user?.id, courseId, course]);

  // Handle course reset
  const handleResetCourse = async () => {
    if (!user?.id || !courseId || isResetting) return;

    setIsResetting(true);
    const success = await resetCourseProgress(user.id, courseId);
    setIsResetting(false);

    if (success) {
      setShowResetMessage(true);
      setCompletionStatus(null);
      setWeaknessCount(0);

      // Hide message after 3 seconds
      setTimeout(() => setShowResetMessage(false), 3000);
    }
  };

  if (isLoading) {
    return <div className="loading">Laden...</div>;
  }

  if (error || !course) {
    return (
      <div className="error-state">
        <p>{error || 'Kurs nicht gefunden'}</p>
        <Link to="/" className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>
      </div>
    );
  }

  // Text-based course (e.g., English grammar)
  const isTextCourse = course.lessonType === 'text';
  const isDeutschCourse = courseId === 'deutsch-schularbeit';
  const hasTopicNavigation = !!(course.categories && course.categories.length > 0);

  // Helper function to format section descriptions with progress
  const getGrammarDesc = () => {
    const total = course.textLessons?.length || 0;
    if (!moduleProgress) return `${total} Kapitel`;
    const read = moduleProgress.lessonsRead;
    if (read === 0) return `${total} Kapitel · noch nicht gelesen`;
    if (read >= total) return `${total} Kapitel · alle gelesen ✓`;
    return `${total} Kapitel · ${read} gelesen`;
  };

  const getFlashcardsDesc = () => {
    const total = course.flashcards.length;
    if (!moduleProgress) return `${total} Karten`;
    const known = moduleProgress.flashcardsKnown;
    if (known === 0) return `${total} Karten · noch nicht geuebt`;
    const pct = Math.round((known / total) * 100);
    return `${total} Karten · ${known} gewusst (${pct}%)`;
  };

  const getQuizDesc = () => {
    const total = course.quizQuestions.length;
    if (!moduleProgress) return `${total} MC-Fragen`;
    if (moduleProgress.quizBest === null) return `${total} MC-Fragen · noch nicht gemacht`;
    return `${total} MC-Fragen · Bester: ${moduleProgress.quizBest}%`;
  };

  const getFillInDesc = () => {
    const total = course.fillInQuestions?.length || 0;
    if (!moduleProgress) return `${total} Uebungen`;
    if (moduleProgress.fillInBest === null) return `${total} Uebungen · noch nicht gemacht`;
    return `${total} Uebungen · Bester: ${moduleProgress.fillInBest}%`;
  };

  const getTransformDesc = () => {
    const total = course.transformQuestions?.length || 0;
    if (!moduleProgress) return `${total} Saetze`;
    if (moduleProgress.transformBest === null) return `${total} Saetze · noch nicht gemacht`;
    return `${total} Saetze · Bester: ${moduleProgress.transformBest}%`;
  };

  const getVocabMatchDesc = () => {
    const total = course.vocabSets?.length || 0;
    if (!moduleProgress) return `${total} Sets`;
    if (!moduleProgress.vocabMatchDone) return `${total} Sets · noch nicht gespielt`;
    return `${total} Sets · gespielt ✓`;
  };

  // Build modules for a specific topic/category (Deutsch course)
  const getTopicModules = (categoryId: string) => {
    const modules: { id: string; title: string; icon: React.ComponentType<{ size: number }>; description: string; path: string; color: string }[] = [];

    // 1. Lektion
    const lesson = course.textLessons?.find(l => l.category === categoryId);
    if (lesson) {
      modules.push({
        id: 'lesson',
        title: 'Lernstoff',
        icon: FileText,
        description: lesson.title,
        path: `/course/${courseId}/text-lessons?category=${categoryId}`,
        color: '#9c27b0',
      });
    }

    // 2. Lernkarten
    const fcCount = course.flashcards.filter(f => f.category === categoryId).length;
    if (fcCount > 0) {
      modules.push({
        id: 'flashcards',
        title: 'Lernkarten',
        icon: CreditCard,
        description: `${fcCount} Karten`,
        path: `/course/${courseId}/flashcards?category=${categoryId}`,
        color: '#ff9800',
      });
    }

    // 3. Vokabel-Matching
    const vsCount = course.vocabSets?.filter(v => v.category === categoryId).length || 0;
    if (vsCount > 0) {
      modules.push({
        id: 'vocab-match',
        title: 'Vokabel-Matching',
        icon: Shuffle,
        description: `${vsCount} ${vsCount === 1 ? 'Set' : 'Sets'}`,
        path: `/course/${courseId}/vocab-match?category=${categoryId}`,
        color: '#4caf50',
      });
    }

    // 4. Lernkarten-Quiz (needs at least 4 flashcards for MC)
    if (fcCount >= 4) {
      modules.push({
        id: 'vocab-quiz',
        title: 'Lernkarten-Quiz',
        icon: Languages,
        description: `${fcCount} Karten als MC`,
        path: `/course/${courseId}/vocab-quiz?category=${categoryId}`,
        color: '#ab47bc',
      });
    }

    // 5. Quiz
    const qCount = course.quizQuestions.filter(q => q.category === categoryId).length;
    if (qCount > 0) {
      modules.push({
        id: 'quiz',
        title: 'Quiz',
        icon: HelpCircle,
        description: `${qCount} MC-Fragen`,
        path: `/course/${courseId}/quiz?category=${categoryId}`,
        color: '#2196f3',
      });
    }

    // 6. Lückentext
    const fiCount = course.fillInQuestions?.filter(q => q.category === categoryId).length || 0;
    if (fiCount > 0) {
      modules.push({
        id: 'fill-in',
        title: 'Lückentext',
        icon: PencilLine,
        description: `${fiCount} Übungen`,
        path: `/course/${courseId}/fill-in?category=${categoryId}`,
        color: '#00bcd4',
      });
    }

    // 7. Rechenaufgaben
    const meCount = course.mathExercises?.filter(m => m.category === categoryId).length || 0;
    if (meCount > 0) {
      modules.push({
        id: 'math-exercise',
        title: 'Rechenaufgaben',
        icon: Calculator,
        description: `${meCount} Aufgaben`,
        path: `/course/${courseId}/math-exercise?category=${categoryId}`,
        color: '#7c4dff',
      });
    }

    // 8. Songs
    const songCount = course.songs?.filter(s => s.category === categoryId).length || 0;
    if (songCount > 0) {
      modules.push({
        id: 'songs',
        title: 'Songs',
        icon: Music,
        description: `${songCount} ${songCount === 1 ? 'Song' : 'Songs'}`,
        path: `/course/${courseId}/songs`,
        color: '#e91e63',
      });
    }

    return modules;
  };

  // Get the selected topic's category info
  const selectedCategory = selectedTopic
    ? course.categories?.find(c => c.id === selectedTopic)
    : null;

  // Build sections for non-Deutsch text courses
  const sections = isTextCourse && !hasTopicNavigation
    ? [
        {
          id: 'text-lessons',
          title: 'Grammatik',
          icon: FileText,
          description: getGrammarDesc(),
          path: `/course/${courseId}/text-lessons`,
          color: '#9c27b0',
        },
        {
          id: 'flashcards',
          title: 'Vokabeln',
          icon: CreditCard,
          description: getFlashcardsDesc(),
          path: `/course/${courseId}/flashcards`,
          color: '#ff9800',
        },
        {
          id: 'vocab-match',
          title: 'Vokabel-Matching',
          icon: Shuffle,
          description: getVocabMatchDesc(),
          path: `/course/${courseId}/vocab-match`,
          color: '#4caf50',
        },
        {
          id: 'vocab-quiz',
          title: 'Vokabel-Quiz',
          icon: Languages,
          description: `${course.flashcards.length} Vokabeln als MC`,
          path: `/course/${courseId}/vocab-quiz`,
          color: '#ab47bc',
        },
        {
          id: 'quiz',
          title: 'Quiz',
          icon: HelpCircle,
          description: getQuizDesc(),
          path: `/course/${courseId}/quiz`,
          color: '#2196f3',
        },
        {
          id: 'fill-in',
          title: 'Lueckentext',
          icon: PencilLine,
          description: getFillInDesc(),
          path: `/course/${courseId}/fill-in`,
          color: '#00bcd4',
        },
        {
          id: 'transform',
          title: 'Indirect Speech',
          icon: MessageSquare,
          description: getTransformDesc(),
          path: `/course/${courseId}/transform`,
          color: '#e91e63',
        },
        // "Frag Claude" chat section
        ...(courseId === 'spanisch-schularbeit' ? [{
          id: 'ask-claude',
          title: 'Frag Claude',
          icon: MessageCircle,
          description: 'Frag die KI wenn du etwas nicht verstehst',
          path: `/course/${courseId}/ask-claude`,
          color: '#ff5722',
        }] : []),
      ].filter(section => {
        // Hide sections with no data
        if (section.id === 'text-lessons' && (!course.textLessons || course.textLessons.length === 0)) return false;
        if (section.id === 'vocab-match' && (!course.vocabSets || course.vocabSets.length === 0)) return false;
        if (section.id === 'fill-in' && (!course.fillInQuestions || course.fillInQuestions.length === 0)) return false;
        if (section.id === 'transform' && (!course.transformQuestions || course.transformQuestions.length === 0)) return false;
        if (section.id === 'quiz' && (!course.quizQuestions || course.quizQuestions.length === 0)) return false;
        if (section.id === 'vocab-quiz' && (!course.flashcards || course.flashcards.length === 0)) return false;
        if (section.id === 'flashcards' && (!course.flashcards || course.flashcards.length === 0)) return false;
        return true;
      })
    : !isTextCourse
    ? [
        // Audio-based course sections (existing)
        {
          id: 'lessons',
          title: 'Lektionen',
          icon: BookOpen,
          description: `${course.lessons.length} Audio-Lektionen`,
          path: `/course/${courseId}/lessons`,
          color: '#4caf50',
        },
        {
          id: 'flashcards',
          title: 'Lernkarten',
          icon: CreditCard,
          description: getFlashcardsDesc(),
          path: `/course/${courseId}/flashcards`,
          color: '#ff9800',
        },
        {
          id: 'vocab-quiz',
          title: 'Vokabel-Quiz',
          icon: Languages,
          description: `${course.flashcards.length} Karten als MC`,
          path: `/course/${courseId}/vocab-quiz`,
          color: '#ab47bc',
        },
        {
          id: 'quiz',
          title: 'Quiz',
          icon: HelpCircle,
          description: getQuizDesc(),
          path: `/course/${courseId}/quiz`,
          color: '#2196f3',
        },
        {
          id: 'mindmaps',
          title: 'Mindmaps',
          icon: Map,
          description: `${course.mindmaps.length} Uebersichten`,
          path: `/course/${courseId}/mindmaps`,
          color: '#9c27b0',
        },
        {
          id: 'written',
          title: 'Schriftlich',
          icon: PenLine,
          description: 'KI-bewertete Fragen',
          path: `/course/${courseId}/written`,
          color: '#e91e63',
        },
      ]
    : []; // Deutsch course uses topic-based navigation instead

  // Calculate days until test
  const daysUntilTest = course.testDate
    ? Math.ceil((new Date(course.testDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="course-view">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
      }}>
        {selectedTopic && hasTopicNavigation ? (
          <button
            onClick={() => setSelectedTopic(null)}
            className="back-link"
            style={{
              marginBottom: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: '#9fa8da',
              fontSize: '14px',
              padding: 0,
            }}
          >
            <ArrowLeft size={18} />
            Zurück zur Übersicht
          </button>
        ) : (
          <Link to="/" className="back-link" style={{ marginBottom: 0 }}>
            <ArrowLeft size={18} />
            Zurück
          </Link>
        )}
        <button
          onClick={() => setShowResetModal(true)}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            padding: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="Fortschritt zurücksetzen"
        >
          <Settings size={20} style={{ color: 'rgba(255,255,255,0.6)' }} />
        </button>
      </div>

      {/* Course header - show topic title when in topic view */}
      {selectedTopic && selectedCategory ? (
        <div className="course-header" style={{ marginBottom: '20px' }}>
          <span className="course-icon-large" style={{ fontSize: '36px' }}>
            {selectedCategory.label.split(' ')[0]}
          </span>
          <div>
            <h1 style={{ fontSize: '22px' }}>{selectedCategory.label.replace(/^[^\s]+\s/, '')}</h1>
            <p className="course-description" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
              {course.title}
            </p>
          </div>
        </div>
      ) : (
        <div className="course-header">
          <span className="course-icon-large">{course.icon}</span>
          <div>
            <h1>{course.title}</h1>
            <p className="course-description">{course.description}</p>
          </div>
        </div>
      )}

      {/* Reset success message */}
      {showResetMessage && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          background: 'rgba(76, 175, 80, 0.15)',
          border: '1px solid rgba(76, 175, 80, 0.3)',
          borderRadius: '12px',
          padding: '14px 16px',
          marginBottom: '16px',
        }}>
          <CheckCircle size={20} style={{ color: '#4caf50' }} />
          <span style={{ color: '#4caf50', fontWeight: 600 }}>
            Alles zurückgesetzt! Viel Spaß beim Wiederholen 💪
          </span>
        </div>
      )}

      {/* "Alles geschafft" celebration card - only for text courses, only in overview */}
      {!selectedTopic && isTextCourse && completionStatus?.allComplete && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 213, 79, 0.2) 0%, rgba(255, 152, 0, 0.15) 100%)',
          border: '2px solid #ffd54f',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '20px',
          textAlign: 'center',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '12px',
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'rgba(255, 213, 79, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Trophy size={32} style={{ color: '#ffd54f' }} />
            </div>
          </div>

          <h2 style={{
            color: '#ffd54f',
            fontSize: '20px',
            fontWeight: 700,
            marginBottom: '8px',
          }}>
            {user?.name || 'Du'}, du hast alles richtig gemacht!
          </h2>

          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '14px',
            lineHeight: 1.5,
            marginBottom: '16px',
          }}>
            Keine offenen Fragen oder Tests mehr – du bist bestens vorbereitet
            {course.testDate && (
              <> für den Test am {new Date(course.testDate).toLocaleDateString('de-AT', {
                day: 'numeric',
                month: 'long',
              })}</>
            )}! 🎉
          </p>

          <p style={{
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '13px',
            marginBottom: '16px',
          }}>
            Willst du alles zurücksetzen und nochmal üben, damit sich alles noch besser festigt?
          </p>

          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            <button
              onClick={handleResetCourse}
              disabled={isResetting}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '10px',
                color: '#e8eaf6',
                fontSize: '14px',
                fontWeight: 600,
                cursor: isResetting ? 'not-allowed' : 'pointer',
                opacity: isResetting ? 0.6 : 1,
              }}
            >
              <RotateCcw size={18} />
              {isResetting ? 'Wird zurückgesetzt...' : 'Kurs wiederholen'}
            </button>

            <button
              onClick={() => setCompletionStatus(prev => prev ? { ...prev, allComplete: false } : null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: '#4caf50',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <CheckCircle size={18} />
              So lassen
            </button>
          </div>
        </div>
      )}

      {/* Test date countdown - only in overview */}
      {!selectedTopic && daysUntilTest !== null && daysUntilTest > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          background: daysUntilTest <= 3 ? 'rgba(239, 83, 80, 0.15)' :
                      daysUntilTest <= 7 ? 'rgba(255, 152, 0, 0.15)' :
                      'rgba(76, 175, 80, 0.1)',
          border: `1px solid ${
            daysUntilTest <= 3 ? 'rgba(239, 83, 80, 0.3)' :
            daysUntilTest <= 7 ? 'rgba(255, 152, 0, 0.3)' :
            'rgba(76, 175, 80, 0.2)'
          }`,
          borderRadius: '12px',
          padding: '12px 16px',
          marginBottom: '20px',
        }}>
          <Calendar size={20} style={{
            color: daysUntilTest <= 3 ? '#ef5350' :
                   daysUntilTest <= 7 ? '#ff9800' : '#4caf50',
          }} />
          <div>
            <div style={{
              color: daysUntilTest <= 3 ? '#ef5350' :
                     daysUntilTest <= 7 ? '#ff9800' : '#4caf50',
              fontSize: '14px',
              fontWeight: 600,
            }}>
              {daysUntilTest === 1 ? 'Morgen ist der Test!' :
               `Noch ${daysUntilTest} Tage bis zum Test`}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
              {new Date(course.testDate!).toLocaleDateString('de-AT', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
            </div>
          </div>
        </div>
      )}

      {/* === DEUTSCH COURSE: Topic-based navigation === */}
      {hasTopicNavigation && !selectedTopic && (
        <>
          {/* Progress & Recommendation Card with clickable topics */}
          {courseId && course && (
            <CourseProgressCard
              courseId={courseId}
              course={course}
              onProgressLoaded={setModuleProgress}
              onTopicClick={setSelectedTopic}
            />
          )}
        </>
      )}

      {/* === DEUTSCH COURSE: Selected topic modules === */}
      {hasTopicNavigation && selectedTopic && (
        <div className="course-sections">
          {getTopicModules(selectedTopic).map(({ id, title, icon: Icon, description, path, color }) => (
            <Link key={id} to={path} className="section-card" style={{
              borderLeft: `4px solid ${color}`,
            }}>
              <div className="section-icon" style={{ color }}>
                <Icon size={24} />
              </div>
              <div className="section-info">
                <h3>{title}</h3>
                <p>{description}</p>
              </div>
              <ChevronRight size={18} style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
            </Link>
          ))}

          {/* Per-topic weakness button */}
          {selectedTopic && (topicWeaknessMap[selectedTopic] || 0) > 0 && (
            <Link
              to={`/course/${courseId}/flashcards?mode=weakness&category=${selectedTopic}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                background: 'rgba(255, 152, 0, 0.15)',
                border: '1px solid rgba(255, 152, 0, 0.3)',
                borderRadius: '12px',
                marginTop: '16px',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                background: 'rgba(255, 152, 0, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
              }}>
                🔄
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  color: '#ff9800',
                  fontSize: '16px',
                  fontWeight: 600,
                }}>
                  Schwächen in diesem Thema üben ({topicWeaknessMap[selectedTopic]})
                </div>
                <div style={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '13px',
                  marginTop: '2px',
                }}>
                  Lernkarten wiederholen
                </div>
              </div>
              <ChevronRight size={18} style={{ color: '#ff9800' }} />
            </Link>
          )}
        </div>
      )}

      {/* === NON-DEUTSCH COURSES: Flat module list === */}
      {!hasTopicNavigation && (
        <>
          {/* Progress & Recommendation Card */}
          {courseId && course && (
            <CourseProgressCard
              courseId={courseId}
              course={course}
              onProgressLoaded={setModuleProgress}
            />
          )}

          <div className="course-sections">
            {sections.map(({ id, title, icon: Icon, description, path, color }) => (
              <Link key={id} to={path} className="section-card" style={{
                borderLeft: `4px solid ${color}`,
              }}>
                <div className="section-icon" style={{ color }}>
                  <Icon size={24} />
                </div>
                <div className="section-info">
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}

      {/* "Frag Claude" button for Spanish course */}
      {courseId === 'spanisch-schularbeit' && (
        <Link
          to={`/course/${courseId}/ask-claude`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px',
            background: 'linear-gradient(135deg, rgba(255, 87, 34, 0.15), rgba(255, 152, 0, 0.1))',
            border: '1px solid rgba(255, 87, 34, 0.3)',
            borderRadius: '12px',
            marginTop: '20px',
            textDecoration: 'none',
            transition: 'all 0.2s ease',
          }}
        >
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255, 87, 34, 0.3), rgba(255, 152, 0, 0.2))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <MessageCircle size={22} style={{ color: '#ff5722' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              color: '#ff5722',
              fontSize: '16px',
              fontWeight: 600,
            }}>
              Frag Claude
            </div>
            <div style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: '13px',
              marginTop: '2px',
            }}>
              Frag die KI wenn du etwas nicht verstehst
            </div>
          </div>
          <ChevronRight size={18} style={{ color: '#ff5722' }} />
        </Link>
      )}

      {/* Global buttons at bottom - always visible */}
      {/* Weakness practice button - only for text courses with vocab sets */}
      {isTextCourse && weaknessCount > 0 && (
        <Link
          to={`/course/${courseId}/vocab-match?weakness=true`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px',
            background: 'rgba(255, 152, 0, 0.15)',
            border: '1px solid rgba(255, 152, 0, 0.3)',
            borderRadius: '12px',
            marginTop: '20px',
            textDecoration: 'none',
            transition: 'all 0.2s ease',
          }}
        >
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: 'rgba(255, 152, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <RefreshCw size={22} style={{ color: '#ff9800' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              color: '#ff9800',
              fontSize: '16px',
              fontWeight: 600,
            }}>
              Schwächen üben
            </div>
            <div style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: '13px',
              marginTop: '2px',
            }}>
              {weaknessCount} Vokabeln wiederholen
            </div>
          </div>
        </Link>
      )}

      {/* Reset Modal */}
      {user?.id && (
        <ResetModal
          isOpen={showResetModal}
          onClose={() => setShowResetModal(false)}
          userId={user.id}
          courseId={courseId}
          courseName={course.title}
          onResetComplete={() => {
            setCompletionStatus(null);
            setWeaknessCount(0);
          }}
        />
      )}
    </div>
  );
}
